import type { AccountStatus } from '@exigo/shared';
import { Button } from '../components/ui/Button';
import styles from './PendingApprovalScreen.module.css';

/**
 * What a signed-in but not-yet-approved account sees instead of the app.
 *
 * Rendered in place of the router, not inside it: every API route except
 * `/auth/session` refuses this account (see apps/api/src/plugins/auth.ts), so
 * letting the router mount would produce a dashboard of failed requests. This
 * screen is the honest version of that state.
 *
 * The copy deliberately does not say who to contact by name — that list lives
 * in Entra and in people's heads, not in a bundle that ships to the browser.
 */
const MESSAGES: Record<Exclude<AccountStatus, 'active'>, { title: string; body: string }> = {
  pending: {
    title: 'Waiting for approval',
    body:
      'Your account has been created and an administrator has been notified. You will have access as soon as someone approves the request — no need to sign up again.',
  },
  rejected: {
    title: 'Access not granted',
    body:
      'An administrator reviewed this request and did not grant access. If you believe that is a mistake, contact whoever looks after MS IncentiveHub for your team.',
  },
  disabled: {
    title: 'Access withdrawn',
    body:
      'This account no longer has access to MS IncentiveHub. Any scenarios you created are retained. Contact an administrator if you need it restored.',
  },
};

interface PendingApprovalScreenProps {
  status: Exclude<AccountStatus, 'active'>;
  email: string;
  onSignOut: () => void;
}

export function PendingApprovalScreen({
  status,
  email,
  onSignOut,
}: PendingApprovalScreenProps): React.JSX.Element {
  const { title, body } = MESSAGES[status];

  return (
    <main className={styles.screen}>
      <div className={styles.card}>
        <span className={styles.mark} aria-hidden="true">
          <ClockIcon />
        </span>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.body}>{body}</p>
        <p className={styles.identity}>
          Signed in as <strong>{email}</strong>
        </p>
        <Button variant="secondary" onClick={onSignOut}>
          Sign out
        </Button>
      </div>
    </main>
  );
}

function ClockIcon(): React.JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 7.5V12L15 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
